import '../../../scss/CNS.scss';
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import 'react-toastify/dist/ReactToastify.css';
import {Controller, FormProvider, useForm, useWatch} from "react-hook-form";
import {DEFAULT_DAAP_APPLICATIONS} from "../cns/constants.js";
import {Box, Stack, Typography} from "@mui/material";
import {Dropdown} from "../../dropdown/Dropdown.jsx";
import DaapAipSubForm from "./components/DaapAipSubForm.jsx";
import {ToastContainer} from "react-toastify";
import DaapDistPlatSubForm from "./components/DaapDistPlatSubForm.jsx";
import {ComboBox} from "../../comboBox/ComboBox.jsx";
import {DataGrid} from "@mui/x-data-grid";
import {TableSkeleton} from "../common/skeleton/TableSkeleton.jsx";
import {useFetchMdmCompanies} from "../data-subscription/DataSubscription.hooks.jsx";
import {useContacts} from "./hooks/DaapSubscription.hooks.jsx";

const COLUMNS = [
    {headerName: "Name", field: "name", flex: 1},
    {headerName: "Email", field: "email", flex: 1},
];

const COMPANY_OPTION_PAGE_SIZE = 1000;
const COMPANY_OPTION_SCROLL_THRESHOLD = 24;

const DEFAULT_FORM_VALUES = {
    selectedApplication: "aip",
    selectedCompany: null,
    selectedContactEmails: [],
    selectedGeographies: [],
    selectedSubcategories: [],
    selectedReport: [],
    selectedDataSource: [],
    selectedDaapCompany: [],
};

export const DaapSubscription = () => {
    const {companyList, isCompaniesLoading} = useFetchMdmCompanies();
    const [companyOptionLimit, setCompanyOptionLimit] = useState(COMPANY_OPTION_PAGE_SIZE);

    const methods = useForm({
        defaultValues:  DEFAULT_FORM_VALUES,
    });

    const {
        control,
        setValue,
    } = methods;

    const selectedApplication = useWatch({
        control,
        name: "selectedApplication",
    });

    const selectedCompany = useWatch({
        control,
        name: "selectedCompany",
    });

    const selectedContactEmails = useWatch({
        control,
        name: "selectedContactEmails",
    }) || [];

    const {data: contactsData, loading: isContactsLoading} = useContacts(selectedCompany?.value);

    const contactRows = useMemo(() => {
        return contactsData?.sdnoContacts?.edges?.map((edge) => ({
            id: edge.node.email,
            name: `${edge.node.fname ?? ""} ${edge.node.lname ?? ""}`,
            email: edge.node.email,
        })) || [];
    }, [contactsData]);

    const hasSelectedContacts = selectedContactEmails.length > 0;
    const hasContacts = contactRows.length > 0;

    const resetSubscriptionSelections = useCallback(() => {
        setValue("selectedGeographies", []);
        setValue("selectedSubcategories", []);
        setValue("selectedReport", []);
        setValue("selectedDataSource", []);
        setValue("selectedDaapCompany", []);
    }, [setValue]);

    const selectedSubscriptionProps = {
        hasSelectedContacts,
        selectedContactEmails,
        selectedCompany,
    };

    const resetCompanyOptionLimit = useCallback(() => {
        setCompanyOptionLimit(COMPANY_OPTION_PAGE_SIZE);
    }, []);

    const handleCompanyListboxScroll = useCallback((event) => {
        const listbox = event.currentTarget;
        const isNearBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - COMPANY_OPTION_SCROLL_THRESHOLD;

        if (!isNearBottom) {
            return;
        }

        setCompanyOptionLimit((currentLimit) => Math.min(
            currentLimit + COMPANY_OPTION_PAGE_SIZE,
            companyList.length
        ));
    }, [companyList.length]);

    const [sortModel, setSortModel] = React.useState([
        {
            field: 'name',
            sort: 'asc',
        },
    ]);

    return (
        <FormProvider {...methods}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                m: "2.5%",
                gap: 1.5,
                border: "1px solid #ccc",
                p: 2,
                borderRadius: 1.5,
            }}>
                <ToastContainer/>
                <Stack spacing={3}>
                    <Typography variant="h6" fontWeight="bold">DAAP Subscription</Typography>

                    <Controller
                        name="selectedApplication"
                        control={control}
                        render={({field}) => (
                            <Dropdown
                                label="Application"
                                data={DEFAULT_DAAP_APPLICATIONS}
                                currentValue={field.value}
                                onChange={(event) => {
                                    field.onChange(event.target.value);
                                    setValue("selectedCompany", null);
                                    setValue("selectedContactEmails", []);
                                    resetSubscriptionSelections();
                                }}
                                required
                            />
                        )}
                    />

                    <Controller
                        name="selectedCompany"
                        control={control}
                        render={({field}) => (
                            <ComboBox
                                isLoading={isCompaniesLoading}
                                label="Company / Organization (SF ID) *"
                                value={field.value}
                                filterOptions={{
                                    limit: companyOptionLimit,
                                    stringify: (option) => `${option.displayName} ${option.value}`,
                                }}
                                data={companyList}
                                isMultiple={false}
                                listboxProps={{
                                    onScroll: handleCompanyListboxScroll,
                                }}
                                onInputChange={resetCompanyOptionLimit}
                                onChange={(_, value) => {
                                    field.onChange(value);
                                    setValue("selectedContactEmails", []);
                                    resetSubscriptionSelections();
                                }}
                                getOptionKeyImplementation={(option) => option.value}
                            />
                        )}
                    />

                    <Box>
                        <Typography variant="subtitle1" gutterBottom>Company Contacts</Typography>

                        {!selectedCompany ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                border={1}
                                padding={2}
                                borderColor="lightgray"
                                gutterBottom
                            >
                                Select a company to view available contacts.
                            </Typography>
                        ) : (
                            <Controller
                                name="selectedContactEmails"
                                control={control}
                                render={({field}) => (
                                    <DataGrid
                                        sortModel={sortModel}
                                        sortingOrder={["asc", "desc"]}
                                        onSortModelChange={(newSortModel) => {
                                            setSortModel(newSortModel.length > 0
                                                ? newSortModel
                                                : [{field: "name", sort: "asc"}]
                                            );
                                        }}
                                        autoHeight
                                        columns={COLUMNS}
                                        rows={contactRows}
                                        loading={isContactsLoading}
                                        getRowId={(row) => row.email}
                                        checkboxSelection
                                        localeText={{
                                            noRowsLabel: "No contacts available.",
                                        }}
                                        slots={{
                                            loadingOverlay: TableSkeleton,
                                        }}
                                        slotProps={{
                                            loadingOverlay: {
                                                columns: COLUMNS,
                                            },
                                        }}
                                        rowSelectionModel={field.value}
                                        onRowSelectionModelChange={(newSelectedEmails) => {
                                            field.onChange(newSelectedEmails);

                                            if (newSelectedEmails.length === 0) {
                                                resetSubscriptionSelections();
                                            }
                                        }}
                                        pageSizeOptions={[10, 25, 50]}
                                        initialState={{
                                            pagination: {
                                                paginationModel: {
                                                    pageSize: 10,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        )}

                        {selectedCompany && hasContacts && !hasSelectedContacts && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{mt: 1}}
                            >
                                Select one or more contacts before adding subscriptions.
                            </Typography>
                        )}
                    </Box>

                    {selectedApplication === "aip" && (
                        <DaapAipSubForm
                            {...selectedSubscriptionProps}
                        />
                    )}
                    {selectedApplication === "distributor-platform" && (
                        <DaapDistPlatSubForm
                            {...selectedSubscriptionProps}
                            resetSubscriptionSelections={resetSubscriptionSelections}
                        />
                    )}
                </Stack>
            </Box>
        </FormProvider>
    );
};
